import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileCheck2,
  FileText,
  HardDrive,
  Headphones,
  KeyRound,
  LockKeyhole,
  MonitorCheck,
  ServerCog,
  ShieldCheck,
  Siren,
  UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';

type StandardStatus = 'Tracked' | 'Evidence gap' | 'External certification required';
type ControlStatus = 'implemented' | 'partial' | 'gap';
type IncidentStatus = 'open' | 'contained' | 'closed';

interface Standard {
  id: string;
  name: string;
  status: StandardStatus;
  score: number;
  owner: string;
  evidence: string[];
  nextStep: string;
}

interface Control {
  id: string;
  name: string;
  area: string;
  status: ControlStatus;
  evidence: string;
  owner: string;
}

interface Incident {
  id: string;
  title: string;
  severity: 'High' | 'Medium' | 'Low';
  status: IncidentStatus;
  openedAt: string;
  owner: string;
  action: string;
}

const standards: Standard[] = [
  {
    id: 'gdpr',
    name: 'UK GDPR',
    status: 'Tracked',
    score: 76,
    owner: 'Information governance',
    evidence: [
      'Accountability register and privacy evidence tracker created',
      'Data protection impact assessment placeholders listed',
      'Access review and data retention controls identified',
    ],
    nextStep: 'Complete DPIA, privacy notices, lawful basis register and processor contracts.',
  },
  {
    id: 'dspt',
    name: 'NHS DSPT 2025-26',
    status: 'Evidence gap',
    score: 52,
    owner: 'DSPT lead',
    evidence: [
      'DSPT evidence areas tracked locally',
      'Security, backups, access control and incident response mapped',
      'No live DSPT submission is connected to this app',
    ],
    nextStep: 'Complete the official DSPT submission with real organisational evidence.',
  },
  {
    id: 'iso27001',
    name: 'ISO/IEC 27001',
    status: 'External certification required',
    score: 44,
    owner: 'Security lead',
    evidence: [
      'ISMS control register started',
      'Risk treatment areas identified',
      'Internal audit and management review process still required',
    ],
    nextStep: 'Create ISMS scope, risk assessment, Statement of Applicability and certification audit plan.',
  },
  {
    id: 'cyber-essentials-plus',
    name: 'Cyber Essentials Plus',
    status: 'External certification required',
    score: 48,
    owner: 'Technical lead',
    evidence: [
      'Technical control checklist started',
      'VPS HTTPS deployment is in place',
      'Device, patching, malware and vulnerability testing evidence still required',
    ],
    nextStep: 'Run accredited Cyber Essentials Plus assessment and remediate technical findings.',
  },
];

const controls: Control[] = [
  { id: 'ctl-1', name: 'Role-based access control', area: 'Access control', status: 'implemented', evidence: 'SUPER_ADMIN, ADMIN, CAREGIVER and FAMILY roles implemented.', owner: 'Technical lead' },
  { id: 'ctl-2', name: 'Superadmin restricted modules', area: 'Access control', status: 'implemented', evidence: 'GP Connect, Workflows, Readiness and Security are superadmin-only.', owner: 'Technical lead' },
  { id: 'ctl-3', name: 'HTTPS and domain isolation', area: 'Infrastructure', status: 'implemented', evidence: 'fuchsiagorleston.com deployed separately on VPS with nginx and SSL.', owner: 'Technical lead' },
  { id: 'ctl-4', name: 'Backups and restore tests', area: 'Resilience', status: 'partial', evidence: 'Backup policy tracker created; restore evidence pending production database.', owner: 'Operations' },
  { id: 'ctl-5', name: 'Security monitoring', area: 'Monitoring', status: 'partial', evidence: 'Monitoring checklist created; SIEM/log shipping not yet connected.', owner: 'Security lead' },
  { id: 'ctl-6', name: 'Incident response playbook', area: 'Incident response', status: 'partial', evidence: 'Incident workflow and severity matrix started.', owner: 'Registered manager' },
  { id: 'ctl-7', name: 'Supplier and processor register', area: 'GDPR', status: 'gap', evidence: 'Register structure present; real supplier contracts need entry.', owner: 'Information governance' },
  { id: 'ctl-8', name: 'Vulnerability management', area: 'Cyber Essentials', status: 'gap', evidence: 'Patch and vulnerability scan evidence not yet uploaded.', owner: 'Technical lead' },
];

const incidents: Incident[] = [
  {
    id: 'sec-001',
    title: 'Failed login spike review',
    severity: 'Medium',
    status: 'contained',
    openedAt: '2026-05-20 09:20',
    owner: 'Technical lead',
    action: 'Review access logs and enable alert thresholds after production auth is connected.',
  },
  {
    id: 'sec-002',
    title: 'Backup restore evidence missing',
    severity: 'High',
    status: 'open',
    openedAt: '2026-05-19 14:00',
    owner: 'Operations',
    action: 'Run restore test after production database is provisioned.',
  },
  {
    id: 'sec-003',
    title: 'Supplier DPA review due',
    severity: 'Low',
    status: 'open',
    openedAt: '2026-05-18 11:15',
    owner: 'Information governance',
    action: 'Collect data processing agreements for hosting, email and monitoring services.',
  },
];

const supportSla = [
  { priority: 'P1 Critical', response: '1 hour', resolution: 'Same day workaround', examples: 'Login outage, suspected breach, medication record outage' },
  { priority: 'P2 High', response: '4 business hours', resolution: '2 business days', examples: 'Reporting failure, GP Connect readiness issue, role access fault' },
  { priority: 'P3 Standard', response: '1 business day', resolution: '5 business days', examples: 'UI issue, workflow change, report template change' },
];

const referenceLinks = [
  { label: 'ICO UK GDPR accountability guidance', url: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/' },
  { label: 'NHS DSPT official service', url: 'https://www.dsptoolkit.nhs.uk/' },
  { label: 'NCSC Cyber Essentials overview', url: 'https://www.ncsc.gov.uk/cyberessentials/overview' },
  { label: 'ISO/IEC 27001 overview', url: 'https://www.iso.org/standard/27001' },
];

const controlStyles: Record<ControlStatus, string> = {
  implemented: 'bg-emerald-100 text-emerald-800',
  partial: 'bg-amber-100 text-amber-800',
  gap: 'bg-red-100 text-red-800',
};

const incidentStyles: Record<Incident['severity'], string> = {
  High: 'bg-red-100 text-red-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low: 'bg-blue-100 text-blue-800',
};

export const EnterpriseSecurity: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'standards' | 'controls' | 'support' | 'incidents' | 'evidence'>('standards');

  const overallScore = Math.round(
    standards.reduce((sum, standard) => sum + standard.score, 0) / standards.length
  );

  const implementedControls = controls.filter((control) => control.status === 'implemented').length;
  const openIncidents = incidents.filter((incident) => incident.status !== 'closed').length;

  const evidencePack = useMemo(() => ({
    generatedAt: new Date().toISOString(),
    provider: 'FUCHSIA HOME CARE CATALIN',
    domain: 'https://fuchsiagorleston.com/',
    standards,
    controls,
    incidents,
    supportSla,
    disclaimer: 'Evidence tracker only. Formal DSPT, ISO 27001 and Cyber Essentials Plus require external submission/audit.',
  }), []);

  const downloadEvidencePack = () => {
    const blob = new Blob([JSON.stringify(evidencePack, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `enterprise-security-evidence-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Security evidence pack exported');
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <LockKeyhole className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Support and Security
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Enterprise Security Hub</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Track GDPR accountability, NHS DSPT, ISO 27001 readiness, Cyber Essentials Plus
                controls, support SLA, backups, monitoring and incident response evidence.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={downloadEvidencePack}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
          >
            <Download className="h-4 w-4" />
            Export Evidence
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Readiness" value={`${overallScore}%`} icon={<ShieldCheck className="h-5 w-5" />} />
        <MetricCard label="Controls implemented" value={`${implementedControls}/${controls.length}`} icon={<CheckCircle2 className="h-5 w-5" />} tone="success" />
        <MetricCard label="Open incidents" value={String(openIncidents)} icon={<Siren className="h-5 w-5" />} tone="warning" />
        <MetricCard label="Standards tracked" value={String(standards.length)} icon={<FileCheck2 className="h-5 w-5" />} />
      </section>

      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex gap-3 text-sm leading-6 text-amber-950">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            This is an internal evidence tracker. It does not mean the organisation is certified
            for ISO 27001, Cyber Essentials Plus, or DSPT. Those require official submissions,
            external assessment and real operational evidence.
          </p>
        </div>
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {[
            { id: 'standards', label: 'Standards' },
            { id: 'controls', label: 'Controls' },
            { id: 'support', label: 'Support SLA' },
            { id: 'incidents', label: 'Incidents' },
            { id: 'evidence', label: 'Evidence Links' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'standards' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {standards.map((standard) => (
            <article key={standard.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{standard.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">Owner: {standard.owner}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  {standard.status}
                </span>
              </div>
              <div className="mt-5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">Readiness</span>
                  <span className="font-semibold text-slate-950">{standard.score}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-[#6c3a80]" style={{ width: `${standard.score}%` }} />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                {standard.evidence.map((item) => (
                  <div key={item} className="flex gap-2 text-sm leading-6 text-slate-600">
                    <CheckCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                {standard.nextStep}
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'controls' && (
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Control</th>
                <th className="px-4 py-3">Area</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Evidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {controls.map((control) => (
                <tr key={control.id}>
                  <td className="px-4 py-3 font-medium text-slate-950">{control.name}</td>
                  <td className="px-4 py-3 text-slate-600">{control.area}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${controlStyles[control.status]}`}>
                      {control.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{control.owner}</td>
                  <td className="px-4 py-3 text-slate-600">{control.evidence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {activeTab === 'support' && (
        <section className="grid gap-4 xl:grid-cols-3">
          {supportSla.map((sla) => (
            <article key={sla.priority} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Headphones className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="font-bold text-slate-950">{sla.priority}</h2>
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <Detail label="Response" value={sla.response} />
                <Detail label="Resolution target" value={sla.resolution} />
                <Detail label="Examples" value={sla.examples} />
              </div>
            </article>
          ))}
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-3">
            <div className="grid gap-4 md:grid-cols-3">
              <SupportControl icon={<HardDrive className="h-5 w-5" />} title="Backups" text="Daily production backup plan with monthly restore test evidence." />
              <SupportControl icon={<MonitorCheck className="h-5 w-5" />} title="Monitoring" text="Availability, error logging and alert thresholds for critical workflows." />
              <SupportControl icon={<ServerCog className="h-5 w-5" />} title="VPS operations" text="Nginx config validation, SSL renewal checks and deployment isolation." />
            </div>
          </article>
        </section>
      )}

      {activeTab === 'incidents' && (
        <section className="grid gap-4 xl:grid-cols-3">
          {incidents.map((incident) => (
            <article key={incident.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-slate-950">{incident.title}</h2>
                  <p className="mt-1 text-sm text-slate-500">{incident.openedAt}</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${incidentStyles[incident.severity]}`}>
                  {incident.severity}
                </span>
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <Detail label="Status" value={incident.status} />
                <Detail label="Owner" value={incident.owner} />
                <Detail label="Action" value={incident.action} />
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'evidence' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {referenceLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-slate-200 bg-white p-5 text-sm font-semibold text-[#6c3a80] shadow-sm hover:bg-slate-50"
            >
              <FileText className="mb-3 h-5 w-5" />
              {link.label}
            </a>
          ))}
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
            <div className="flex items-start gap-3">
              <UserCheck className="mt-1 h-5 w-5 text-[#6c3a80]" />
              <div>
                <h2 className="font-bold text-slate-950">Evidence Pack Contents</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  The export contains tracked standards, controls, open security incidents, support
                  SLA and operational notes. It is suitable for internal preparation before formal
                  DSPT submission or external certification audit.
                </p>
              </div>
            </div>
          </article>
        </section>
      )}
    </div>
  );
};

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const styles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${styles[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

function SupportControl({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-3">
        <div className="text-[#6c3a80]">{icon}</div>
        <h3 className="font-semibold text-slate-950">{title}</h3>
      </div>
      <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
    </div>
  );
}
